/**
 * AGC: Console log helper utilities shared between the Eruda provider and tests.
 */
export type ConsoleLevel = 'log' | 'info' | 'warn' | 'error'

export interface CapturedConsoleLog {
  level: ConsoleLevel
  message: string
  timestamp: string
}

export interface ClipboardFormatOptions {
  filterLevels?: ConsoleLevel[]
  emptyFallback?: string
}

const DEFAULT_FALLBACK = 'AGC: No console logs captured yet.'

/**
 * AGC: Filters captured logs by the provided set of levels.
 */
export const filterLogsByLevel = (
  logs: CapturedConsoleLog[],
  levels?: ConsoleLevel[],
): CapturedConsoleLog[] => {
  if (!levels || levels.length === 0) {
    return logs
  }

  const levelSet = new Set(levels)
  return logs.filter((log) => levelSet.has(log.level))
}

/**
 * AGC: Formats captured logs into a clipboard-friendly block of text.
 */
export const buildConsoleClipboardText = (
  logs: CapturedConsoleLog[],
  options?: ClipboardFormatOptions,
): string => {
  const filtered = filterLogsByLevel(logs, options?.filterLevels)

  if (filtered.length === 0) {
    return options?.emptyFallback ?? DEFAULT_FALLBACK
  }

  return filtered
    .map((log) => {
      const timestamp = new Date(log.timestamp).toLocaleTimeString()
      const level = log.level.toUpperCase()
      return `[${timestamp}][${level}] ${log.message}`
    })
    .join('\n')
}

